import { FieldTemplate, FieldUpdateConfig, UpdateType } from '@cdw-ae/common-ui-components-bulk-field-update';
import { TreeNode } from '@cdw-ae/common-ui-components-tree';
import {
  ASSET_ID_INVALID_CHARS_PATTERN,
  ASSET_ID_PATTERN_ERROR_MESSAGE,
  DEVICE_FIELD_LIMITS,
} from '@cdw-ae/common-data';

/**
 * Available bulk edit field names
 * This ensures type safety when referencing fields in action mappings
 */
export type BulkEditFieldName =
  | 'orgUnitPath'
  | 'status'
  | 'notes'
  | 'annotatedAssetId'
  | 'annotatedUser'
  | 'annotatedLocation';

/**
 * Available device action types
 */
export type DeviceAction =
  | 'move-ou'
  | 'disable'
  | 'enable'
  | 'deprovision'
  | 'profile-wipe'
  | 'factory-reset'
  | 'edit-import';

/**
 * EDUS-2626: actions for which the bulk-action drawer surfaces the
 * "selection may span multiple pages" reminder above the Next button.
 */
export const CROSS_PAGE_REMINDER_ACTIONS: ReadonlySet<DeviceAction> = new Set<DeviceAction>([
  'move-ou',
  'edit-import',
  'disable',
  'enable',
]);

export const CROSS_PAGE_REMINDER_TEXT =
  'You may have devices selected on multiple pages. Please review devices before continuing.';

/**
 * Default field values for specific actions
 */
export interface FieldDefaultValue {
  fieldName: BulkEditFieldName;
  updateType: UpdateType;
  value: unknown;
}

/**
 * Maps device actions to their visible bulk edit fields
 * Type-safe: action keys and field names are validated at compile time
 */
export const ACTION_FIELD_MAP: Record<DeviceAction, BulkEditFieldName[]> = {
  'move-ou': ['orgUnitPath'],
  'disable': ['status'],
  'enable': ['status'],
  'deprovision': ['status'],
  // EDUS-2163 Phase 4 §4a — command-only actions: no field updates, drawer
  // injects the device command per row in executeBulkUpdate.
  'profile-wipe': [],
  'factory-reset': [],
  'edit-import': ['notes', 'annotatedAssetId', 'annotatedUser', 'annotatedLocation'],
};

/**
 * Maps device actions to their default field values
 * Type-safe: only includes actions that need pre-filled values
 */
export const ACTION_DEFAULT_VALUES: Partial<Record<DeviceAction, FieldDefaultValue[]>> = {
  'move-ou': [
    {
      fieldName: 'orgUnitPath',
      updateType: UpdateType.REPLACE,
      value: undefined // User must select OU, but "Move to" is pre-selected
    }
  ],
  'disable': [
    {
      fieldName: 'status',
      updateType: UpdateType.REPLACE,
      value: 'DISABLED'
    }
  ],
  'enable': [
    {
      fieldName: 'status',
      updateType: UpdateType.REPLACE,
      value: 'ACTIVE'
    }
  ],
  'deprovision': [
    {
      fieldName: 'status',
      updateType: UpdateType.REPLACE,
      value: 'DEPROVISIONED'
    }
  ]
};

/**
 * Build bulk edit field configurations
 * @param ouTreeData Organization unit tree data for the OU picker
 * @returns Array of field update configurations
 */
export function buildBulkEditFieldConfigs(ouTreeData: TreeNode[]): FieldUpdateConfig[] {
  return [
    // Organization Unit field
    {
      name: { displayName: 'Organization Unit', value: 'orgUnitPath' satisfies BulkEditFieldName },
      updateTypes: [
        { displayName: 'Keep as is', value: UpdateType.KEEP, shouldDisplayTemplate: false },
        { displayName: 'Move to', value: UpdateType.REPLACE, shouldDisplayTemplate: true }
      ],
      selectOptions: ouTreeData,
      template: FieldTemplate.TREE
    },
    // Device Status field
    {
      name: { displayName: 'Device Status', value: 'status' satisfies BulkEditFieldName },
      updateTypes: [
        { displayName: 'Keep as is', value: UpdateType.KEEP, shouldDisplayTemplate: false },
        { displayName: 'Change to', value: UpdateType.REPLACE, shouldDisplayTemplate: true }
      ],
      selectOptions: [
        { displayName: 'Active', value: 'ACTIVE' },
        { displayName: 'Disabled', value: 'DISABLED' },
        { displayName: 'Deprovisioned', value: 'DEPROVISIONED' }
      ],
      template: FieldTemplate.SELECT
    },
    // Notes field
    {
      name: { displayName: 'Notes', value: 'notes' satisfies BulkEditFieldName },
      updateTypes: [
        { displayName: 'Keep as is', value: UpdateType.KEEP, shouldDisplayTemplate: false },
        { displayName: 'Replace', value: UpdateType.REPLACE, shouldDisplayTemplate: true },
        { displayName: 'Append', value: UpdateType.APPEND, shouldDisplayTemplate: true },
        { displayName: 'Prepend', value: UpdateType.PREPEND, shouldDisplayTemplate: true }
      ],
      template: FieldTemplate.TEXT,
      validation: {
        maxLength: DEVICE_FIELD_LIMITS.notes,
        supportsAppendPrepend: true
      }
    },
    // Asset ID field
    {
      name: { displayName: 'Asset ID', value: 'annotatedAssetId' satisfies BulkEditFieldName },
      updateTypes: [
        { displayName: 'Keep as is', value: UpdateType.KEEP, shouldDisplayTemplate: false },
        { displayName: 'Replace', value: UpdateType.REPLACE, shouldDisplayTemplate: true }
      ],
      template: FieldTemplate.TEXT,
      validation: {
        maxLength: DEVICE_FIELD_LIMITS.annotatedAssetId,
        pattern: ASSET_ID_INVALID_CHARS_PATTERN,
        patternErrorMessage: ASSET_ID_PATTERN_ERROR_MESSAGE
      }
    },
    // Annotated User field
    {
      name: { displayName: 'Annotated User', value: 'annotatedUser' satisfies BulkEditFieldName },
      updateTypes: [
        { displayName: 'Keep as is', value: UpdateType.KEEP, shouldDisplayTemplate: false },
        { displayName: 'Replace', value: UpdateType.REPLACE, shouldDisplayTemplate: true }
      ],
      template: FieldTemplate.TEXT,
      validation: {
        maxLength: DEVICE_FIELD_LIMITS.annotatedUser
      }
    },
    // Annotated Location field
    {
      name: { displayName: 'Location', value: 'annotatedLocation' satisfies BulkEditFieldName },
      updateTypes: [
        { displayName: 'Keep as is', value: UpdateType.KEEP, shouldDisplayTemplate: false },
        { displayName: 'Replace', value: UpdateType.REPLACE, shouldDisplayTemplate: true }
      ],
      template: FieldTemplate.TEXT,
      validation: {
        maxLength: DEVICE_FIELD_LIMITS.annotatedLocation
      }
    }
  ];
}

/**
 * Type guard to check if a string is a valid DeviceAction
 */
export function isDeviceAction(action: string): action is DeviceAction {
  return action in ACTION_FIELD_MAP;
}

/**
 * Get the list of actions that use the bulk edit drawer
 */
export function getBulkEditActions(): DeviceAction[] {
  return Object.keys(ACTION_FIELD_MAP) as DeviceAction[];
}

/**
 * Unified bulk edit configuration for an action
 * Contains both visible fields and their default values
 */
export interface BulkEditActionConfig {
  visibleFields: BulkEditFieldName[];
  defaultValues?: FieldDefaultValue[];
  actionType?: DeviceAction;
}

/**
 * Get visible fields for a specific action
 * Returns undefined if action is not a valid bulk edit action
 * @deprecated Use getBulkEditConfigForAction instead
 */
export function getVisibleFieldsForAction(action: string): BulkEditFieldName[] | undefined {
  if (isDeviceAction(action)) {
    return ACTION_FIELD_MAP[action];
  }
  return undefined;
}

/**
 * Get default field values for a specific action
 * Returns undefined if action has no default values
 * @deprecated Use getBulkEditConfigForAction instead
 */
export function getDefaultValuesForAction(action: string): FieldDefaultValue[] | undefined {
  if (isDeviceAction(action)) {
    return ACTION_DEFAULT_VALUES[action];
  }
  return undefined;
}

/**
 * Get unified bulk edit configuration for a specific action
 * Returns both visible fields and default values in a single object
 * Returns undefined if action is not a valid bulk edit action
 */
export function getBulkEditConfigForAction(action: string): BulkEditActionConfig | undefined {
  if (isDeviceAction(action)) {
    return {
      visibleFields: ACTION_FIELD_MAP[action],
      defaultValues: ACTION_DEFAULT_VALUES[action],
      actionType: action
    };
  }
  return undefined;
}
